import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Stack, 
  Paper,
  TextField,
  Button,
  Alert,
  IconButton,
  InputAdornment,
  Collapse
} from '@mui/material';
import { Shield, User, Eye, EyeOff, Check, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Map des labels de rôles
const roleLabels = {
  CLIENT: 'Client',
  CLIENTADMIN: 'Admin Client',
  DEV: 'Développeur',
  TECHNICIEN: 'Technicien',
  ADMIN: 'Administrateur',
  SUPERADMIN: 'SuperAdmin'
};

function Settings() {
  const { user } = useAuth();
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handlePasswordChange = (field) => (e) => {
    setPasswordData({ ...passwordData, [field]: e.target.value });
    setError('');
    setSuccess('');
  };

  const toggleShowPassword = (field) => {
    setShowPasswords({ ...showPasswords, [field]: !showPasswords[field] });
  };

  const validatePassword = () => {
    if (!passwordData.currentPassword) {
      setError('Veuillez entrer votre mot de passe actuel.');
      return false;
    }
    if (passwordData.newPassword.length < 8) {
      setError('Le nouveau mot de passe doit contenir au moins 8 caractères.');
      return false;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return false;
    }
    if (passwordData.currentPassword === passwordData.newPassword) {
      setError('Le nouveau mot de passe doit être différent de l\'ancien.');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validatePassword()) return;

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Mot de passe modifié avec succès.');
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setShowPasswordForm(false);
      } else {
        setError(data.error || 'Erreur lors du changement de mot de passe.');
      }
    } catch (err) {
      setError('Erreur réseau. Veuillez réessayer.');
    }

    setLoading(false);
  };

  const handleCancel = () => {
    setShowPasswordForm(false);
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setError('');
  };

  // Indicateurs de force du mot de passe
  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: 'inherit' };
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    if (strength <= 2) return { strength, label: 'Faible', color: 'error.main' };
    if (strength <= 3) return { strength, label: 'Moyen', color: 'warning.main' };
    return { strength, label: 'Fort', color: 'success.main' };
  };

  const passwordStrength = getPasswordStrength(passwordData.newPassword);

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 4 }}>Paramètres</Typography>

      <Stack spacing={3}>
        {/* Success message global */}
        {success && (
          <Alert 
            severity="success" 
            icon={<Check size={20} />}
            onClose={() => setSuccess('')}
          >
            {success}
          </Alert>
        )}

        {/* Mon Compte */}
        <Card sx={{ bgcolor: 'background.paper', backgroundImage: 'none', borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
              <Box sx={{ p: 1, bgcolor: 'rgba(0, 212, 170, 0.1)', borderRadius: 2, color: 'primary.main' }}>
                <User size={24} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Mon Compte</Typography>
            </Stack>
            <Box sx={{ pl: 7 }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>{user.firstName} {user.lastName}</Typography>
              <Typography variant="body2" color="text.secondary">{user.email}</Typography>
              <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ px: 1, py: 0.5, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1, border: '1px solid rgba(255,255,255,0.1)' }}>
                  Rôle: {roleLabels[user.role] || user.role}
                </Typography>
                {user.companyName && (
                  <Typography variant="caption" sx={{ px: 1, py: 0.5, bgcolor: 'rgba(0, 212, 170, 0.1)', borderRadius: 1, border: '1px solid rgba(0, 212, 170, 0.2)', color: 'primary.main' }}>
                    {user.companyName}
                  </Typography>
                )}
              </Stack>
            </Box>
          </CardContent>
        </Card>

        {/* Note notifications */}
        <Paper sx={{ p: 3, bgcolor: 'rgba(0, 212, 170, 0.05)', border: '1px solid rgba(0, 212, 170, 0.2)', borderRadius: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Note sur les notifications</Typography>
          <Typography variant="body2" color="text.secondary">
            Les notifications sont désormais gérées par soumission. 
            Un administrateur peut choisir quels collègues seront notifiés pour chaque projet directement dans la vue détaillée de la soumission.
          </Typography>
        </Paper>

        {/* Sécurité - Changement de mot de passe */}
        <Card sx={{ bgcolor: 'background.paper', backgroundImage: 'none', borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ p: 1, bgcolor: 'rgba(99, 102, 241, 0.1)', borderRadius: 2, color: '#6366f1' }}>
                  <Shield size={24} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Sécurité</Typography>
              </Stack>
              {!showPasswordForm && (
                <Button 
                  variant="outlined" 
                  size="small"
                  startIcon={<Lock size={16} />}
                  onClick={() => setShowPasswordForm(true)}
                >
                  Changer le mot de passe
                </Button>
              )}
            </Stack>

            <Box sx={{ pl: 7 }}>
              <Collapse in={!showPasswordForm}>
                <Typography variant="body2" color="text.secondary">
                  Protégez votre compte en utilisant un mot de passe fort et unique.
                </Typography>
              </Collapse>

              <Collapse in={showPasswordForm}>
                <Stack spacing={3}>
                  {error && (
                    <Alert severity="error" onClose={() => setError('')}>
                      {error}
                    </Alert>
                  )}

                  {/* Mot de passe actuel */}
                  <TextField
                    label="Mot de passe actuel"
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange('currentPassword')}
                    fullWidth
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => toggleShowPassword('current')}
                            edge="end"
                          >
                            {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />

                  {/* Nouveau mot de passe */}
                  <Box>
                    <TextField
                      label="Nouveau mot de passe"
                      type={showPasswords.new ? 'text' : 'password'}
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange('newPassword')}
                      fullWidth
                      size="small"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => toggleShowPassword('new')}
                              edge="end"
                            >
                              {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                    {passwordData.newPassword && (
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                        <Box sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
                          <Box 
                            sx={{ 
                              width: `${(passwordStrength.strength / 5) * 100}%`, 
                              height: '100%', 
                              bgcolor: passwordStrength.color,
                              transition: 'width 0.3s, background-color 0.3s'
                            }} 
                          />
                        </Box>
                        <Typography variant="caption" sx={{ color: passwordStrength.color, minWidth: 50 }}>
                          {passwordStrength.label}
                        </Typography>
                      </Stack>
                    )}
                  </Box>

                  {/* Confirmer le mot de passe */}
                  <TextField
                    label="Confirmer le nouveau mot de passe"
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange('confirmPassword')}
                    fullWidth
                    size="small"
                    error={passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword}
                    helperText={
                      passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword 
                        ? 'Les mots de passe ne correspondent pas' 
                        : ''
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => toggleShowPassword('confirm')}
                            edge="end"
                          >
                            {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />

                  {/* Actions */}
                  <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button 
                      variant="text" 
                      color="inherit" 
                      onClick={handleCancel}
                      disabled={loading}
                    >
                      Annuler
                    </Button>
                    <Button 
                      variant="contained" 
                      onClick={handleSubmit}
                      disabled={loading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                    >
                      {loading ? 'Modification...' : 'Modifier le mot de passe'}
                    </Button>
                  </Stack>

                  {/* Conseils */}
                  <Paper sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                    <Typography variant="caption" color="text.secondary" component="div">
                      <strong>Conseils pour un mot de passe sécurisé :</strong>
                      <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                        <li>Au moins 8 caractères (12+ recommandé)</li>
                        <li>Mélange de majuscules et minuscules</li>
                        <li>Au moins un chiffre</li>
                        <li>Au moins un caractère spécial (!@#$%...)</li>
                      </ul>
                    </Typography>
                  </Paper>
                </Stack>
              </Collapse>
            </Box>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}

export default Settings;
